import React, { useState, useEffect } from 'react'
import SpaceTheme from '../components/SpaceTheme'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  RadioGroup,
  FormControlLabel,
  Radio,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Quiz as QuizIcon,
  Star as StarIcon,
  Favorite as FavoriteIcon,
  Check as CheckIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { db } from '../firebase/config'
import { doc, getDoc, addDoc, collection } from 'firebase/firestore'
import toast from 'react-hot-toast'

const WordMatchingGame = ({ taskId, taskTitle, onBack }) => {
  const { user } = useAuth()
  const [words, setWords] = useState([])
  const [shuffledWords, setShuffledWords] = useState([])
  const [currentWordIndex, setCurrentWordIndex] = useState(0)
  const [currentWord, setCurrentWord] = useState(null)
  const [options, setOptions] = useState([])
  const [selectedAnswer, setSelectedAnswer] = useState('')
  const [correctAnswers, setCorrectAnswers] = useState(0)
  const [totalQuestions, setTotalQuestions] = useState(0)
  const [loading, setLoading] = useState(true)
  const [gameStarted, setGameStarted] = useState(false)
  const [showAnswerDialog, setShowAnswerDialog] = useState(false)
  const [answerFeedback, setAnswerFeedback] = useState({ isCorrect: false, correctAnswer: '' })
  const [showResultDialog, setShowResultDialog] = useState(false)
  const [gameComplete, setGameComplete] = useState(false)

  useEffect(() => {
    if (taskId) {
      loadWordTask()
    }
  }, [taskId])

  const loadWordTask = async () => {
    try {
      setLoading(true)
      
      const taskDoc = await getDoc(doc(db, 'wordTasks', taskId))
      
      if (taskDoc.exists()) {
        const data = taskDoc.data()
        const wordsData = data.words || []
        
        if (wordsData.length > 0) {
          setWords(wordsData)
          startGame(wordsData)
        } else {
          toast.error('Bu görevde kelime bulunamadı')
          onBack()
        }
      } else {
        toast.error('Görev bulunamadı')
        onBack()
      }
    } catch (error) {
      console.error('Error loading word task:', error)
      toast.error('Kelime görevi yüklenirken hata oluştu')
      onBack()
    } finally {
      setLoading(false)
    }
  }

  const startGame = (wordsData) => {
    // Android uygulamasıyla aynı mantık
    setTotalQuestions(wordsData.length)
    const shuffled = [...wordsData].sort(() => Math.random() - 0.5)
    setShuffledWords(shuffled)
    setCurrentWordIndex(0)
    setCorrectAnswers(0)
    setGameStarted(true)
    setGameComplete(false)
    showNextQuestion(0, shuffled, wordsData)
  }

  const showNextQuestion = (questionIndex, shuffledWordsArray, allWordsArray) => {
    if (questionIndex >= shuffledWordsArray.length) {
      finishGame()
      return
    }

    const word = shuffledWordsArray[questionIndex]
    setCurrentWord(word)
    setSelectedAnswer('')
    
    // Seçenekleri oluştur (Android uygulamasıyla aynı mantık)
    createOptions(word, allWordsArray)
  }

  const createOptions = (correctWord, allWordsArray) => {
    // Doğru cevabı ekle
    const optionsList = [correctWord.turkish || correctWord.meaning]
    
    // Yanlış seçenekler için diğer kelimelerden anlamlar al
    const wrongOptions = []
    for (const word of allWordsArray) {
      const meaning = word.turkish || word.meaning
      if (meaning !== (correctWord.turkish || correctWord.meaning) && !wrongOptions.includes(meaning)) {
        wrongOptions.push(meaning)
      }
    }
    
    // 3 yanlış seçenek ekle (Android uygulamasıyla aynı)
    const shuffledWrong = [...wrongOptions].sort(() => Math.random() - 0.5)
    for (let i = 0; i < Math.min(3, shuffledWrong.length); i++) {
      optionsList.push(shuffledWrong[i])
    }
    
    // Seçenekleri karıştır
    const shuffledOptions = [...optionsList].sort(() => Math.random() - 0.5)
    setOptions(shuffledOptions)
  }

  const handleAnswerChange = (event) => {
    setSelectedAnswer(event.target.value)
  }

  const handleSubmitAnswer = () => {
    if (!selectedAnswer) {
      toast.warning('Lütfen bir seçenek seçin')
      return
    }

    // Doğru cevabı kontrol et
    const correctMeaning = currentWord.turkish || currentWord.meaning
    const isCorrect = selectedAnswer === correctMeaning
    
    if (isCorrect) {
      setCorrectAnswers(correctAnswers + 1)
    }
    
    setAnswerFeedback({
      isCorrect,
      correctAnswer: correctMeaning
    })
    setShowAnswerDialog(true)
  }

  const handleAnswerDialogClose = () => {
    setShowAnswerDialog(false)
    
    if (currentWordIndex >= totalQuestions - 1) {
      // Oyun tamamlandı
      finishGame()
    } else {
      // Sonraki soruya geç
      const nextIndex = currentWordIndex + 1
      setCurrentWordIndex(nextIndex)
      showNextQuestion(nextIndex, shuffledWords, words)
    }
  }

  const finishGame = () => {
    setGameComplete(true)
    setShowResultDialog(true)
    
    // Sonucu Firestore'a kaydet (Android uygulamasıyla aynı)
    saveGameResult()
  }

  const saveGameResult = async () => {
    try {
      const studentId = user.uid
      
      // Önce word task'ın teacherId'sini al (Android uygulamasıyla aynı)
      const taskDoc = await getDoc(doc(db, 'wordTasks', taskId))
      
      if (taskDoc.exists()) {
        const taskData = taskDoc.data()
        const teacherId = taskData.teacherId
        const classId = taskData.classId
        const className = taskData.className
        
        const result = {
          wordTaskId: taskId,
          studentId: studentId,
          teacherId: teacherId,
          classId: classId,
          className: className,
          score: correctAnswers,
          totalQuestions: totalQuestions,
          completionDate: new Date(),
          taskMode: 'matching'
        }
        
        await addDoc(collection(db, 'wordTaskResults'), result)
        console.log('Eşleştirme oyunu sonucu kaydedildi:', result)
      }
    } catch (error) {
      console.error('Error saving game result:', error)
      toast.error('Sonuç kaydedilirken hata oluştu')
    }
  }

  const handleGameComplete = () => {
    setShowResultDialog(false)
    toast.success('Eşleştirme oyunu başarıyla tamamlandı!')
    onBack()
  }

  const getScoreColor = () => {
    const percentage = (correctAnswers / totalQuestions) * 100
    if (percentage >= 80) return 'success.main'
    if (percentage >= 60) return 'warning.main'
    return 'error.main'
  }

  const getScoreEmoji = () => {
    const percentage = (correctAnswers / totalQuestions) * 100
    if (percentage >= 90) return '🎉'
    if (percentage >= 80) return '😊'
    if (percentage >= 60) return '😐'
    return '😔'
  }

  if (loading) {
    return (
      <SpaceTheme>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static" sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                {taskTitle || 'Kelime Eşleştirme'}
              </Typography>
            </Toolbar>
          </AppBar>
          <Container maxWidth="md" sx={{ py: 3 }}>
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <LinearProgress sx={{ mb: 2 }} />
              <Typography>Eşleştirme oyunu yükleniyor...</Typography>
            </Box>
          </Container>
        </Box>
      </SpaceTheme>
    )
  }

  if (!gameStarted || words.length === 0) {
    return (
      <SpaceTheme>
        <Box sx={{ flexGrow: 1 }}>
          <AppBar position="static" sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
          }}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
                Oyun Hatası
              </Typography>
            </Toolbar>
          </AppBar>
          <Container maxWidth="md" sx={{ py: 3 }}>
            <Alert severity="error">
              Eşleştirme oyunu başlatılamadı. Lütfen daha sonra tekrar deneyin.
            </Alert>
          </Container>
        </Box>
      </SpaceTheme>
    )
  }

  const progress = ((currentWordIndex + 1) / totalQuestions) * 100

  return (
    <SpaceTheme>
      <Box sx={{ flexGrow: 1 }}>
        {/* App Bar */}
        <AppBar position="static" sx={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          backdropFilter: 'blur(10px)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)'
        }}>
          <Toolbar>
            <IconButton edge="start" color="inherit" onClick={onBack} sx={{ mr: 2 }}>
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              {taskTitle || 'Kelime Eşleştirme'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <StarIcon sx={{ color: '#FFD700' }} />
              <Typography variant="body2">
                {correctAnswers}/{currentWordIndex}
              </Typography>
            </Box>
          </Toolbar>
        </AppBar>

        {/* Main Content */}
        <Container maxWidth="md" sx={{ py: 3 }}>
          {/* Progress Card */}
          <Card sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}>
            <CardContent sx={{ pb: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Soru {currentWordIndex + 1} / {totalQuestions}
                </Typography>
                <Chip 
                  icon={<StarIcon />} 
                  label={`${correctAnswers} Doğru`} 
                  color="primary" 
                  size="small" 
                />
              </Box>
              <LinearProgress variant="determinate" value={progress} sx={{ height: 8, borderRadius: 4 }} />
            </CardContent>
          </Card>

          {/* Question Card */}
          <Card sx={{ 
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.2)',
          }}>
            <CardContent>
              <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
                🔤 Bu kelimenin anlamını seçin:
              </Typography>
              <Typography variant="h3" fontWeight="bold" color="text.primary" sx={{ 
                textAlign: 'center', 
                my: 4, 
                p: 3,
                backgroundColor: 'rgba(103, 110, 234, 0.1)',
                borderRadius: 3,
                border: '2px solid rgba(103, 110, 234, 0.3)'
              }}>
                {currentWord?.english || currentWord?.word || ''}
              </Typography>

              <RadioGroup value={selectedAnswer} onChange={handleAnswerChange}>
                {options.map((option, index) => (
                  <FormControlLabel
                    key={index}
                    value={option}
                    control={<Radio />}
                    label={option}
                    sx={{
                      mb: 1,
                      p: 2,
                      borderRadius: 3,
                      border: '1px solid rgba(0, 0, 0, 0.1)',
                      backgroundColor: selectedAnswer === option ? 'rgba(103, 110, 234, 0.1)' : 'transparent',
                      '&:hover': {
                        backgroundColor: 'rgba(0, 0, 0, 0.05)'
                      }
                    }}
                  />
                ))}
              </RadioGroup>

              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleSubmitAnswer}
                  disabled={!selectedAnswer}
                  startIcon={<CheckIcon />}
                  sx={{ minWidth: 200 }}
                >
                  Cevabı Kontrol Et
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Container>

        {/* Answer Feedback Dialog */}
        <Dialog open={showAnswerDialog} onClose={handleAnswerDialogClose}>
          <DialogTitle sx={{ textAlign: 'center' }}>
            {answerFeedback.isCorrect ? '✅ Doğru!' : '❌ Yanlış!'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ textAlign: 'center' }}>
              {answerFeedback.isCorrect 
                ? 'Tebrikler, doğru cevap verdiniz!' 
                : `Yanlış cevap! Doğru cevap: ${answerFeedback.correctAnswer}`
              }
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button onClick={handleAnswerDialogClose} variant="contained">
              {currentWordIndex >= totalQuestions - 1 ? 'Sonuçları Gör' : 'Devam Et'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Final Result Dialog */}
        <Dialog open={showResultDialog} onClose={handleGameComplete}>
          <DialogTitle sx={{ textAlign: 'center' }}>
            {getScoreEmoji()} Oyun Tamamlandı!
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <Typography variant="h4" color={getScoreColor()} sx={{ mb: 2 }}>
              {correctAnswers}/{totalQuestions}
            </Typography>
            <Typography variant="h6" sx={{ mb: 1 }}>
              Doğru cevap oranı: %{Math.round((correctAnswers / totalQuestions) * 100)}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {correctAnswers === totalQuestions 
                ? 'Mükemmel! Tüm soruları doğru cevapladınız!' 
                : correctAnswers >= totalQuestions * 0.8 
                ? 'Harika! Çok başarılı bir performans!' 
                : correctAnswers >= totalQuestions * 0.6 
                ? 'İyi! Biraz daha çalışarak daha da iyi olabilirsiniz.'
                : 'Bu kelimeleri daha fazla çalışmanız gerekiyor.'
              }
            </Typography>
          </DialogContent>
          <DialogActions sx={{ justifyContent: 'center' }}>
            <Button onClick={handleGameComplete} variant="contained" size="large">
              Tamam
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </SpaceTheme>
  )
}

export default WordMatchingGame
